import React, { useState, useEffect } from 'react';
import { 
  ThumbsUp, 
  ThumbsDown, 
  Building, 
  Search, 
  AlertCircle, 
  Loader2,
  Check,
  X,
  ArrowRight,
  Filter,
  RefreshCw
} from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { masterSupabase } from '@/lib/supabase-master.js';

/**
 * TransactionPropertySuggestion Component
 * 
 * Displays AI-generated suggestions for mapping transactions to properties
 * and allows users to provide feedback on these suggestions.
 */
const TransactionPropertySuggestion = ({ open, onClose, ownerId }) => {
  // States
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  // Data states
  const [transactions, setTransactions] = useState([]);
  const [properties, setProperties] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [keywords, setKeywords] = useState([]);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('pending'); // pending, accepted, rejected
  const [filterProperty, setFilterProperty] = useState('all');
  
  // Load data on component mount
  useEffect(() => {
    if (open) {
      loadData();
    }
  }, [open, ownerId]);
  
  // Load all necessary data
  const loadData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Load transactions
      const { data: transactionsData, error: transactionsError } = await masterSupabase
        .from('transactions')
        .select('id, description, amount, date, bank_account_id')
        .eq("owner_id", ownerId)
        .order('date', { ascending: false })
        .limit(100);
      
      if (transactionsError) throw new Error('Fehler beim Laden der Transaktionen');
      
      // Load properties
      const { data: propertiesData, error: propertiesError } = await masterSupabase
        .from('properties')
        .select('id, name, address')
        .eq("owner_id", ownerId);
      
      if (propertiesError) throw new Error('Fehler beim Laden der Immobilien');
      
      // Load suggestions
      const { data: suggestionsData, error: suggestionsError } = await masterSupabase
        .from('transaction_property_suggestions')
        .select('*')
        .eq("owner_id", ownerId);
      
      if (suggestionsError) throw new Error('Fehler beim Laden der Vorschläge');
      
      // Load keywords
      const { data: keywordsData, error: keywordsError } = await masterSupabase
        .from('property_keywords')
        .select('*')
        .eq("owner_id", ownerId);
      
      if (keywordsError) throw new Error('Fehler beim Laden der Schlüsselwörter');
      
      setTransactions(transactionsData || []);
      setProperties(propertiesData || []);
      setSuggestions(suggestionsData || []);
      setKeywords(keywordsData || []);
    } catch (error) {
      console.error('Error loading data:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Generate suggestions for transactions
  const generateSuggestions = async () => {
    setIsProcessing(true);
    setError(null);
    
    try {
      // Get transactions without suggestions
      const transactionsWithoutSuggestions = transactions.filter(transaction => 
        !suggestions.some(suggestion => suggestion.transaction_id === transaction.id)
      );
      
      if (transactionsWithoutSuggestions.length === 0) {
        setSuccess('Alle Transaktionen haben bereits Vorschläge');
        setIsProcessing(false);
        return;
      }
      
      // Process each transaction
      for (const transaction of transactionsWithoutSuggestions) {
        // Find matching keywords in transaction description
        const matchingKeywords = keywords.filter(keyword => 
          transaction.description.toLowerCase().includes(keyword.keyword.toLowerCase())
        );
        
        if (matchingKeywords.length > 0) {
          // Sort by weight
          matchingKeywords.sort((a, b) => b.weight - a.weight);
          
          // Create suggestion
          const suggestion = {
            transaction_id: transaction.id,
            property_id: matchingKeywords[0].property_id,
            confidence_score: calculateConfidenceScore(matchingKeywords),
            status: 'pending',
            owner_id: ownerId,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          };
          
          // Save suggestion
          const { error: insertError } = await masterSupabase
            .from('transaction_property_suggestions')
            .insert([suggestion]);
          
          if (insertError) throw new Error('Fehler beim Speichern der Vorschläge');
        }
      }
      
      // Reload data
      await loadData();
      setSuccess('Vorschläge erfolgreich generiert');
    } catch (error) {
      console.error('Error generating suggestions:', error);
      setError(error.message);
    } finally {
      setIsProcessing(false);
    }
  };
  
  // Calculate confidence score based on matching keywords
  const calculateConfidenceScore = (matchingKeywords) => {
    if (matchingKeywords.length === 0) return 0;
    
    // Sum of weights
    const totalWeight = matchingKeywords.reduce((sum, keyword) => sum + keyword.weight, 0);
    
    // Normalize to 0-100
    return Math.min(Math.round(totalWeight * 10), 100);
  };
  
  // Handle suggestion feedback
  const handleFeedback = async (suggestionId, status) => {
    try {
      // Update suggestion status
      const { error: updateError } = await masterSupabase
        .from('transaction_property_suggestions')
        .update({ status, updated_at: new Date().toISOString() })
        .eq('id', suggestionId);
      
      if (updateError) throw new Error('Fehler beim Aktualisieren des Vorschlags');
      
      // Update local state
      setSuggestions(prev => 
        prev.map(suggestion => 
          suggestion.id === suggestionId 
            ? { ...suggestion, status } 
            : suggestion
        )
      );
      
      // If accepted, we could also update the transaction with the property_id
      // This would depend on your data model
    } catch (error) {
      console.error('Error updating suggestion:', error);
      setError(error.message);
    }
  };
  
  // Handle manual property assignment
  const handleManualAssignment = async (suggestionId, propertyId) => {
    try {
      // Update suggestion
      const { error: updateError } = await masterSupabase
        .from('transaction_property_suggestions')
        .update({ 
          property_id: propertyId,
          status: 'accepted',
          updated_at: new Date().toISOString() 
        })
        .eq('id', suggestionId);
      
      if (updateError) throw new Error('Fehler beim Aktualisieren des Vorschlags');
      
      // Update local state
      setSuggestions(prev => 
        prev.map(suggestion => 
          suggestion.id === suggestionId 
            ? { ...suggestion, property_id: propertyId, status: 'accepted' } 
            : suggestion
        )
      );
    } catch (error) {
      console.error('Error updating suggestion:', error);
      setError(error.message);
    }
  };
  
  // Get property name by ID
  const getPropertyName = (propertyId) => {
    const property = properties.find(p => p.id === propertyId);
    return property ? property.property_name : 'Unbekannte Immobilie';
  };
  
  // Get transaction by ID
  const getTransaction = (transactionId) => {
    return transactions.find(t => t.id === transactionId) || null;
  };
  
  // Filter suggestions
  const filteredSuggestions = suggestions.filter(suggestion => {
    const transaction = getTransaction(suggestion.transaction_id);
    if (!transaction) return false;
    
    // Filter by search term
    const matchesSearch = searchTerm === '' || 
      transaction.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Filter by status
    const matchesStatus = filterStatus === 'all' || suggestion.status === filterStatus;
    
    // Filter by property
    const matchesProperty = filterProperty === 'all' || suggestion.property_id === filterProperty;
    
    return matchesSearch && matchesStatus && matchesProperty;
  });
  
  // Get confidence level class
  const getConfidenceLevelClass = (score) => {
    if (score >= 80) return 'bg-green-100 text-green-800 border-green-200';
    if (score >= 50) return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    return 'bg-red-100 text-red-800 border-red-200';
  };
  
  // Format amount
  const formatAmount = (amount) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };
  
  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('de-DE');
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Transaktions-Objekt-Vorschläge</DialogTitle>
          <DialogDescription>
            KI-generierte Vorschläge für die Zuordnung von Transaktionen zu Immobilien
          </DialogDescription>
        </DialogHeader>
        
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
            <span className="ml-2">Lade Daten...</span>
          </div>
        ) : (
          <div className="space-y-6">
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Fehler</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            
            {success && (
              <Alert className="bg-green-50 text-green-800 border-green-200">
                <Check className="h-4 w-4" />
                <AlertTitle>Erfolg</AlertTitle>
                <AlertDescription>{success}</AlertDescription>
              </Alert>
            )}
            
            {/* Controls */}
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Transaktionen durchsuchen..."
                    className="pl-9"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
              
              <div className="flex gap-2">
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-[140px]">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle Status</SelectItem>
                    <SelectItem value="pending">Ausstehend</SelectItem>
                    <SelectItem value="accepted">Akzeptiert</SelectItem>
                    <SelectItem value="rejected">Abgelehnt</SelectItem>
                  </SelectContent>
                </Select>
                
                <Select value={filterProperty} onValueChange={setFilterProperty}>
                  <SelectTrigger className="w-[180px]">
                    <Building className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Immobilie" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle Immobilien</SelectItem>
                    {properties.map(property => (
                      <SelectItem key={property.id} value={property.id}>
                        {property.property_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Button 
                  onClick={generateSuggestions} 
                  disabled={isProcessing}
                  className="flex items-center gap-2"
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Generiere...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4" />
                      Vorschläge generieren
                    </>
                  )}
                </Button>
              </div>
            </div>
            
            {/* Suggestions List */}
            <div className="space-y-4">
              {filteredSuggestions.length === 0 ? (
                <div className="text-center py-8">
                  <Building className="w-12 h-12 mx-auto text-muted-foreground" />
                  <h3 className="mt-4 text-lg font-medium">Keine Vorschläge gefunden</h3>
                  <p className="text-muted-foreground mt-2">
                    {suggestions.length === 0 
                      ? 'Klicken Sie auf "Vorschläge generieren", um Transaktionen automatisch zuzuordnen'
                      : 'Keine Vorschläge entsprechen den aktuellen Filterkriterien'}
                  </p>
                </div>
              ) : (
                filteredSuggestions.map(suggestion => {
                  const transaction = getTransaction(suggestion.transaction_id);
                  if (!transaction) return null;
                  
                  return (
                    <Card key={suggestion.id} className="overflow-hidden">
                      <CardHeader className={`pb-3 ${
                        suggestion.status === 'accepted' ? 'bg-green-50' :
                        suggestion.status === 'rejected' ? 'bg-red-50' :
                        'bg-muted/50'
                      }`}>
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-base truncate">
                            {transaction.description}
                          </CardTitle>
                          <Badge className={getConfidenceLevelClass(suggestion.confidence_score)}>
                            {suggestion.confidence_score}% Übereinstimmung
                          </Badge>
                        </div>
                        <CardDescription className="flex items-center justify-between">
                          <span>{formatDate(transaction.date)}</span>
                          <span className={transaction.amount >= 0 ? 'text-green-600' : 'text-red-600'}>
                            {formatAmount(transaction.amount)}
                          </span>
                        </CardDescription>
                      </CardHeader>
                      
                      <CardContent className="pt-3">
                        <div className="flex items-center gap-2">
                          <Building className="w-4 h-4 text-muted-foreground" />
                          <span className="font-medium">Vorgeschlagene Immobilie:</span>
                          <span>{getPropertyName(suggestion.property_id)}</span>
                        </div>
                        
                        {suggestion.status === 'pending' && (
                          <div className="mt-4">
                            <p className="text-sm text-muted-foreground mb-2">Andere Immobilien zuordnen:</p>
                            <div className="flex flex-wrap gap-2">
                              {properties
                                .filter(property => property.id !== suggestion.property_id)
                                .map(property => (
                                  <Badge 
                                    key={property.id} 
                                    variant="outline"
                                    className="cursor-pointer hover:bg-primary hover:text-primary-foreground"
                                    onClick={() => handleManualAssignment(suggestion.id, property.id)}
                                  >
                                    {property.property_name}
                                  </Badge>
                                ))
                              }
                            </div>
                          </div>
                        )}
                      </CardContent>
                      
                      <CardFooter className="pt-0">
                        <div className="flex justify-end gap-2 w-full">
                          {suggestion.status === 'pending' ? (
                            <>
                              <Button 
                                variant="outline" 
                                size="sm"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => handleFeedback(suggestion.id, 'rejected')}
                              >
                                <ThumbsDown className="w-4 h-4 mr-1" />
                                Ablehnen
                              </Button>
                              <Button 
                                variant="outline" 
                                size="sm"
                                className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                onClick={() => handleFeedback(suggestion.id, 'accepted')}
                              >
                                <ThumbsUp className="w-4 h-4 mr-1" />
                                Akzeptieren
                              </Button>
                            </>
                          ) : (
                            <Badge className={
                              suggestion.status === 'accepted' 
                                ? 'bg-green-100 text-green-800 border-green-200' 
                                : 'bg-red-100 text-red-800 border-red-200'
                            }>
                              {suggestion.status === 'accepted' ? (
                                <div className="flex items-center gap-1">
                                  <Check className="w-3 h-3" />
                                  Akzeptiert
                                </div>
                              ) : (
                                <div className="flex items-center gap-1">
                                  <X className="w-3 h-3" />
                                  Abgelehnt
                                </div>
                              )}
                            </Badge>
                          )}
                        </div>
                      </CardFooter>
                    </Card>
                  );
                })
              )}
            </div>
            
            {/* Pagination or Load More */}
            {filteredSuggestions.length > 0 && filteredSuggestions.length < suggestions.length && (
              <div className="flex justify-center pt-4">
                <Button variant="outline" size="sm">
                  Mehr laden
                </Button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TransactionPropertySuggestion;

